import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import toast from 'react-hot-toast'
import { ProfileMatch } from './types'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

export enum ProfileMatchPreference {
  None = 'NONE',
  Favorite = 'FAVORITE',
  Blocked = 'BLOCKED',
}

interface SetProfileMatchMetadataParams {
  id: string
  preference?: ProfileMatchPreference
  notes?: string
}

export const useSetProfileMatchMetadata = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: SetProfileMatchMetadataParams) => {
      const response = await fetchWithAuth(
        `${URL}/profile/${params.id}/match-metadata`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            preference: params.preference,
            notes: params.notes,
          }),
        }
      )
      return response
    },
    onMutate: async (params: SetProfileMatchMetadataParams) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['profile', params.id],
      })
      await queryClient.cancelQueries({
        queryKey: ['profileMatches'],
      })

      // Snapshot the previous values
      const previousProfile = queryClient.getQueryData(['profile', params.id])
      const previousProfileMatches = queryClient.getQueryData([
        'profileMatches',
      ])

      // Optimistically update profile data
      if (params.preference !== undefined) {
        queryClient.setQueryData(['profile', params.id], (old: any) => {
          if (!old) return old
          const isFavorite =
            params.preference === ProfileMatchPreference.Favorite
          return {
            ...old,
            favorite: isFavorite,
          }
        })
      }

      // Optimistically update profile matches
      if (params.preference !== undefined) {
        queryClient.setQueryData(['profileMatches'], (old: any) => {
          if (!old?.matches) return old
          const isFavorite =
            params.preference === ProfileMatchPreference.Favorite

          return {
            ...old,
            matches: old.matches.map((match: ProfileMatch) =>
              match.id === params.id
                ? { ...match, favorite: isFavorite }
                : match
            ),
          }
        })
      }

      return { previousProfile, previousProfileMatches }
    },
    onSuccess: (data, variables) => {
      // Invalidate queries to ensure consistency with the server
      queryClient.invalidateQueries({
        queryKey: ['profile', variables.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['profileMatches'],
      })
    },
    onError: (error, variables, context) => {
      // Revert optimistic updates on error
      if (context) {
        queryClient.setQueryData(
          ['profile', variables.id],
          context.previousProfile
        )
        queryClient.setQueryData(
          ['profileMatches'],
          context.previousProfileMatches
        )
      }
      console.error('Error updating profile match metadata:', error)
      toast.error('Failed to update profile metadata')
    },
  })
}
